import { InputBaseComponentProps } from '@material-ui/core';

//==========================================//
//=============== List Types ===============//
//==========================================//

export interface ListCell {
    title: string;
    data: any;
    customStyle?: React.CSSProperties;
    type?: FormatType;
    optional?: boolean;
    readOnly?: boolean;
    customValue?: React.ReactNode;
    emptyCell?: boolean;
    hideCell?: boolean;
    copyable?: boolean; // Show a button next to the label to copy the value
}

export interface ViewEditCell extends ListCell {
    id: string;
    regex?: RegExp; // tested without spaces when the cell has a format
    format?: InputFormatter;
    // A full-width band that heads the cells below it rather than a cell of its
    // own: `title` is its label, `data` whatever it shows beside it.
    sectionCell?: boolean;
    cellType?: 'Picker' | 'Checkbox' | 'Boolean';
    options?: FormPickerDatas[];
    linked?: FormLinkTo | FormLinkTo[];
    placeholder?: string;
    hint?: string;
    inputsAttributs?: InputBaseComponentProps;
}

// FORMAT
export enum FormatType {
    String = 1,
    Picker,
    PickerMultiple,
    BooleanChips,
    Chips,
    DateShort,
    DateLong,
    Price,
    Number,
    Address,
    Boolean,
    BooleanString,
    Phone,
    Email,
    Action,
    Autocomplete,
    Object,
    Registration,
}

// TABLE
export interface HeadCell {
    id: any;
    idSearch?: string;
    idSort?: any;
    defaultSort?: boolean;
    disabled?: boolean;
    label: string;
    numeric?: boolean;
    type?: FormatType;
    sortable?: boolean;
    filterable?: boolean;
    placeholder?: string;
    contains?: boolean;
    pickerOptions?: PickerOption[];
    chipsOptions?: any;
    chipsTest?: Function;
    interval?: boolean;
    intervalKeys?: IntervalKeys;
    intervalEnd?: string;
    intervalDateTime?: boolean;
    cellProps?: Object;
    noLink?: boolean;
    action?: Function;
    formatCell?: Function;
    loaderCell?: Function;
    api?: string;
    isUuid?: boolean;
    customAlt?: any; // Function
    customValue?: Function;
    minLength?: number;
    defaultVisibility?: boolean;
}

export interface IntervalKeys {
    start: string;
    end: string;
}

export interface PickerOption {
    value: string;
    label: string;
    icon?: string;
}

//==========================================//
//=============== Form Types ===============//
//==========================================//
export interface FormDatas {
    title?: string;
    subtitle?: string;
    sectionStyle?: React.CSSProperties;
    datas?: InputDatas[];
    customView?: JSX.Element;
    computeRules?: ComputeObject[];
}
export interface FormPickerDatas {
    id: string;
    name: string;
}

export interface ChipDatas {
    id: string;
    label: string;
}

export interface InputDatas {
    id: string; // has to be uniq or it may has strange behavior
    name?: string;
    label: string;
    hint?: string;
    placeholder?: string;
    options?: FormPickerDatas[];
    selected?: FormPickerDatas[];
    noEmpty?: boolean;
    searchable?: boolean; // Picker with a text field to filter the options (case and accent insensitive)
    optionsGroups?: JSX.Element;
    customView?: Function;
    inputList?: FormPickerDatas[];
    boxes?: any[];
    required?: boolean;
    type?: InputType;
    disableTimestampConvertion?: boolean;
    dateOutputFormat?: string;
    numberNegative?: boolean;
    onlyNegative?: boolean;
    hidden?: boolean;
    disable?: boolean;
    disabledBy?: FormDisableBy;
    linked?: FormLinkTo | FormLinkTo[];
    linkDate?: FormLinkDate | FormLinkDate[];
    startAdornment?: JSX.Element;
    endAdornment?: JSX.Element;
    inputsAttributs?: InputBaseComponentProps;
    defaultValue?: string;
    switchOnLabel?: string;
    switchTest?: (value) => boolean;
    chipsOptions?: ChipDatas[];
    onFieldChange?: (event, value?, shouldCallChange?) => void;
    onBlurOnly?: boolean;
    style?: React.CSSProperties;
    icon?: string;
    customButtonText?: string;
    onClick?: (datas, setValues) => void;
    regex?: 'default' | RegExp;
    format?: InputFormatter; // text inputs only
}

// Rewrites what is typed or pasted (e.g. editIban), and says where the caret goes
export type InputFormatter = (
    raw: string,
    caret: number,
    previous?: string,
    inputType?: string,
) => { value: string; caret: number };

export interface DefaultFormData {
    id: string; // has to be uniq or it may has strange behavior
    name: string;
    label: string;
    hint?: JSX.Element;
    disabled?: boolean;
    required?: boolean;
    regex?: 'default' | RegExp;
    placeholder?: string;
    style?: React.CSSProperties;
    handleChange?: (event, value?, shouldCallChange?) => void;
}

export interface FormLinkDate {
    id: string;
    type: 'min' | 'max';
    value: number;
    next?: boolean; // if true, the next state will be used to compute the value
    isFullDate?: boolean; // if true, the date will be formatted as a full date (e.g., 'YYYY-MM-DD HH:mm:ss')
}

export interface FormLinkTo {
    id: string;
    value: string | boolean | string[];
}

export interface FormDisableBy {
    id: string;
    value: any;
}

export interface Condition {
    id: string;
    isBoolean: boolean;
}

export interface ComputeHint {
    id: string;
    compute: (nextState: any, newVal: any) => any;
}

export interface ComputeObject {
    target: string;
    by: string;
    conditions?: Condition[];
    compute?: (nextState: any, newVal: any) => any;
    computeHint?: ComputeHint[];
}

type typeString = string & { type?: any };
export type InputType =
    | 'date'
    | 'datetime-local'
    | 'month'
    | 'boolean'
    | 'number'
    | 'email'
    | 'tel'
    | 'textarea'
    | 'switch'
    | 'chips'
    | 'file'
    | 'files'
    | 'customButton' // Custom button not input
    | typeString;

//==========================================//
//=============== XX Types ===============//
//==========================================//

export interface ActionFunction {
    func: (e?) => void;
    label: string;
    icon?: React.ReactNode; // Can be any React component (e.g., Material-UI icons)
    style?: React.CSSProperties;
}

export interface ActionCustom {
    label: string;
    customView: JSX.Element;
}

export type Action = ActionCustom | ActionFunction;

export interface Anchor {
    href: string;
    label: string;
}

export interface BubbleInfo {
    text: string;
    visible: boolean;
    tooltip?: string;
    colorEnable?: boolean;
}

export interface HeaderProps {
    icon?: any; //null: no, "default": default, other: other
    title?: string;
    subtitle?: string;
    bubble?: BubbleInfo;
    anchors?: Anchor[];
    actions?: Action[];
    additionalActions?: ActionFunction[];
    button?: JSX.Element;
}
